import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useRouteParams } from '@/contexts/RouteContext';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { CampaignForm, type CampaignFormSubmitData } from './campaign-form';
import { buildUpdateCampaignInput } from './campaign-input';
import { CAMPAIGN_QUERY, CAMPAIGN_UPDATE_MUTATION } from './campaigns.gql.tsx';

interface CampaignQueryResponse {
	campaign?: {
		campaignId: string;
		name: string;
		description?: string | null;
		startDate: string;
		endDate: string;
		isActive: boolean;
	} | null;
}

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;

export const CampaignsEdit = () => {
	const { id } = useRouteParams();
	const go = useGo();
	useEditPageHeader({ id, resourceName: 'campaigns' });

	const isValidUlid = useMemo(() => {
		return typeof id === 'string' && ULID_REGEX.test(id.toUpperCase());
	}, [id]);

	const queryEnabled = Boolean(id && isValidUlid);
	const { data, loading, error, refetch } = useQuery<CampaignQueryResponse>(
		CAMPAIGN_QUERY,
		{
			variables: { id },
			skip: !queryEnabled
		}
	);

	const [updateCampaign, { loading: isSubmitting }] = useMutation(
		CAMPAIGN_UPDATE_MUTATION
	);

	const campaign = useMemo(() => {
		if (!id) return null;
		const record = data?.campaign;
		if (!record || record.campaignId !== id) {
			return null;
		}

		return {
			campaignId: record.campaignId,
			name: record.name ?? '',
			description: record.description ?? '',
			startDate: record.startDate,
			endDate: record.endDate,
			isActive: record.isActive
		};
	}, [data?.campaign, id]);

	const handleSubmit = async (values: CampaignFormSubmitData) => {
		if (!campaign) {
			showMutationErrorToast('campaigns', 'update', { message: 'Unable to resolve the campaign identifier.' });
			return;
		}

		const payload = buildUpdateCampaignInput(values, campaign.campaignId);

		try {
			const { data: mutationData } = await updateCampaign(
				getMutationOptions('campaigns', { input: payload })
			);

			const message = extractCheckResultError(
				mutationData?.updateCampaign
			);
			if (message) {
				showMutationErrorToast('campaigns', 'update', { message });
				return;
			}

			showMutationSuccessToast('campaigns', 'update');
			// Navigate back with the edited campaign ID for selection
			go({
				to: '/campaigns',
				state: { selectCampaignId: campaign.campaignId }
			});
		} catch (error) {
			showMutationErrorToast('campaigns', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Campaign not found</AlertTitle>
					<AlertDescription>
						The campaign identifier is missing from the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid) {
		return (
			<div className="p-6">
				<Card className="space-y-4">
					<h2 className="text-lg font-semibold text-destructive">
						Invalid campaign identifier
					</h2>
					<p className="text-sm text-muted-foreground">
						The provided campaign id{' '}
						<code className="px-2 py-1 rounded bg-muted">{id}</code>{' '}
						is not a valid ULID value.
					</p>
					<Button
						variant="outline"
						onClick={() => go({ to: '/campaigns' })}
					>
						Back to campaigns
					</Button>
				</Card>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-3">
					<Button
						variant="outline"
						onClick={() => go({ to: '/campaigns' })}
					>
						← Back
					</Button>
					<h1 className="text-2xl font-semibold">Edit campaign</h1>
				</div>
				<Card className="text-sm text-muted-foreground">
					Loading campaign details...
				</Card>
			</div>
		);
	}

	if (error || !campaign) {
		return (
			<div className="p-4">
				<Alert variant="destructive">
					<AlertTitle>Unable to load campaign</AlertTitle>
					<AlertDescription>
						{error?.message ??
							'The requested campaign could not be found.'}
					</AlertDescription>
				</Alert>
				<div className="mt-4">
					<Button variant="outline" onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			</div>
		);
	}

	return (
		<FormPageLayout>
			<CampaignForm
				key={`edit-campaign-${campaign?.campaignId ?? 'unknown'}`}
				mode="edit"
				initialData={campaign ?? undefined}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Edit campaign"
			/>
		</FormPageLayout>
	);
};
